import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import styled, { css } from 'styled-components';

import { useViewTransitionState } from '#src/hooks/useViewTransitionState.ts';

import { useMeasureHeaderHeight } from './useMeasureHeaderHeight.ts';

const Sticky = styled.div`
  position: sticky;
  top: var(--current-stack-height, 0px);

  z-index: 1;
  isolation: isolate;
  background: var(--c-black);
  &.transparent {
    background: unset;
  }
`;

const Wrapper = styled.div`
  isolation: isolate;

  &.disableSticky {
    ${Sticky} {
      position: static;
    }
  }

  /**
   * force the sticky elements to be included in the animations on the page
   * to avoid having z-index issues
   */
  ${Sticky} {
    view-transition-name: sticky-header;
  }
  & & ${Sticky} {
    view-transition-name: none;
  }
`;

const SwapVar = styled.div<{ $height: number | undefined }>`
  ${({ $height }) => css`
    --parent-stack-height: calc(
      var(--current-stack-height, 0px) + ${$height || 0}px
    );
  `};
`;
const Content = styled.div`
  --current-stack-height: var(--parent-stack-height, 0px);
  z-index: 0;
`;

export const resetStack = css`
  --parent-stack-height: 0px;
  --current-stack-height: 0px;
`;

export const ContentWithStickyHeader: React.FC<{
  children?: React.ReactNode;
  header: React.ReactNode;
  className?: string;
  hideHeader?: boolean;
  transparentHeader?: boolean;
  disableSticky?: boolean;
}> = ({
  header,
  children,
  className,
  hideHeader,
  transparentHeader,
  disableSticky = false,
}): ReactNode => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { height, measurementReady } = useMeasureHeaderHeight(headerRef);
  const isTransitioning = useViewTransitionState('*');
  const hideWhileMeasuring = !measurementReady && !isTransitioning;

  if (hideHeader) {
    return children;
  }
  return (
    <Wrapper
      className={classNames(className, {
        invisible: hideWhileMeasuring,
        disableSticky,
      })}
    >
      <Sticky
        ref={headerRef}
        className={classNames({ transparent: transparentHeader })}
      >
        {header}
      </Sticky>
      <SwapVar $height={height}>
        <Content>{children}</Content>
      </SwapVar>
    </Wrapper>
  );
};
